import { Module } from 'vuex'
import {
  WhatsAppTemplate,
  PhoneNumber
} from '../types/WhatsAppSettings'
import { whatsAppService, whatsAppSaasInstance } from '../../../services/WhatsAppService'
import { RootState } from '../../../store/state_models'
import config from '@/config'
import { Location } from '@/models'
import firebase from 'firebase/compat/app'


// Store state interface
export interface WhatsAppSettingsStoreModule {
  isConfigured: boolean | null
  isSubscribed: boolean | null
  templates: Array<WhatsAppTemplate>
  phoneNumbers: Array<PhoneNumber>
}

export const whatsAppSettings: Module<
  WhatsAppSettingsStoreModule,
  RootState
> = {
  namespaced: true,
  state: {
    isConfigured: null,
    isSubscribed: null,
    templates: [],
    phoneNumbers: []
  } as WhatsAppSettingsStoreModule,
  getters: {
    hasDefaultNumber: state => {
      const number = state.phoneNumbers.find(phoneNumber => phoneNumber.default == true)
      return number !== undefined ? true : false
    },
  },
  mutations: {
    setIsConfigured(state, value: { isConfigured: boolean }) {
      state.isConfigured = value.isConfigured
    },
    setIsSubscribed(state, value: boolean) {
      state.isSubscribed = value
    },
    setPhoneNumbers(state, value: Array<PhoneNumber>) {
      state.phoneNumbers = value
    },
    setDefaultPhoneNumber(state, value: string) {
      state.phoneNumbers.forEach(phoneNumber => {
        phoneNumber.default = false;
      })
      const phoneNumber = state.phoneNumbers.find(phoneNumber => phoneNumber.id === value)
      if (phoneNumber) {
        phoneNumber.default = true;
      }
    },
    setTemplates(state, value: Array<WhatsAppTemplate>) {
      state.templates = value
    }
  },
  actions: {
    async isConfigured({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.isConfigured(locationId)
        commit('setIsConfigured', res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async onboard() {
      return new Promise((resolve, _reject) => {
        FB.login(
          function (response: { authResponse?: { accessToken: string } }) {
            if (response.authResponse) {
              const accessToken = response.authResponse.accessToken
              resolve(accessToken)
            } else {
              resolve(null)
            }
          },
          {
            scope: "business_management, whatsapp_business_management, whatsapp_business_messaging",
            extras: {
              feature: "whatsapp_embedded_signup",
              version: 2,
              sessionInfoVersion: 2,
              setup: {},
            },
          }
        )
      })
    },
    async onboardComplete({commit, rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const companyId =  rootState?.company.company.id as string

        const res = await whatsAppService.onboard({
          locationId,
          companyId,
          inputToken: data.inputToken
        })
        commit('setIsConfigured', {
          isConfigured: true
        })
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async resync({dispatch, rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        await whatsAppService.resync({
          locationId
        })
        dispatch('getPhoneNumbers')
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getAccountInfo({rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const data = await whatsAppService.getAccountInfo({
          locationId
        })
        return Promise.resolve(data)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getPhoneNumbers({commit, rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getWhatsAppPhoneNumbersForLocationId(locationId)
        commit('setPhoneNumbers', res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async makeDefaultPhoneNumber({commit, rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.makeDefaultPhoneNumber(locationId, data.id)
        commit('setDefaultPhoneNumber', data.id);
        return Promise.resolve(res);
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async getWhatsAppTemplates({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getWhatsAppTemplatesForLocationId(locationId)
        commit('setTemplates', res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async createWhatsAppTemplate({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.createWhatsAppTemplateForLocationId(locationId, data)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async updateWhatsAppTemplate({rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.updateWhatsAppTemplateForLocationId(locationId, data.templateId, data.data)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async deleteWhatsAppTemplate({rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.deleteWhatsAppTemplateForLocationId(locationId, data.templateId, data.templateName)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async getProduct() {
      try {
        const res = await whatsAppService.getProduct()
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async getLocationPricing({rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getLocationResellingConfiguration(locationId)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async locationSub({dispatch, commit, rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.locationSub(locationId)
        if (res.paymentStatus === 'COMPLETE') {
          commit('setIsSubscribed', true)
        }
        dispatch('isConfigured')
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async getCompanySubscriptions({rootState}) {
      try {
        const companyId =  rootState?.company.company.id as string
        const res = await whatsAppService.getCompanySubscriptions(companyId)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async getLocationSubscriptions({commit, dispatch, rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getLocationSubscriptions(locationId)
        commit('setIsSubscribed', res.length === 0 ? false : true)
        if (res.length !== 0) {
          dispatch('isConfigured')
        }
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async paymentMethods({rootState}) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppSaasInstance.paymentMethods(locationId)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async updateLocationEmailId({}, data: any) {
      const location = new Location(data.data)
      location.ref.update({
        email: data.email,
        date_updated: firebase.firestore.FieldValue.serverTimestamp()
      })
    },
    async updateLocationStripeCustomerId({}, data: any) {
      const location = new Location(data.data)
      location.ref.update({
        'settings.saas_settings.stripe_customer_id': data.stripe_customer_id,
        date_updated: firebase.firestore.FieldValue.serverTimestamp()
      })
    },
    getStripeKey() {
      return config.stripeKey
    },
    async createCustomerIfNotExists({rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppSaasInstance.createCustomerIfNotExists(locationId, data)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async setupStripeIntent({rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppSaasInstance.setupStripeIntent(locationId, data)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async paymentMethodExists({rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppSaasInstance.paymentMethodExists(locationId, data)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    },
    async updateCard({rootState}, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppSaasInstance.updateCard(locationId, data)
        return Promise.resolve(res)
      } catch(error) {
        return Promise.reject(error)
      }
    }
  },
}
